import React, {ReactElement, ReactNode, useCallback} from 'react'
import {FormSection} from "../../../common/components/SectionedForm";
import {api} from "../../../common/api";
import {useSelector} from "react-redux";
import {useTranslation} from "react-i18next";
import PaperStack from "../../../common/form/PaperStack";
import FormGroup from "../../../common/form/FormGroup";
import {LabelValues, LabelValueType} from "../../../common/components/LabelValues";
import {CallCenterAgentSettings} from "../../../app/types";
import {useFormSubmissionButtons} from "../../../common/form/useFormSubmissmionButtons";
import {SupportAgent} from "@mui/icons-material";
import {meSelector} from "../../../authentication/redux";
import {useFetchData} from "../../../common/servercall/useFetchData";
import {CallCenterAgentForm} from "./callCenterAgentSettingsForm";

export const useCallCenterAgentSettingsSection = (): FormSection<CallCenterAgentSettings> => {
    const {t} = useTranslation()
    const me = useSelector(meSelector)
    const {buildFormButtons} = useFormSubmissionButtons(false)

    const fetchData = useCallback(
        async (): Promise<CallCenterAgentSettings | undefined> => {
            if (!me) return
            const {data} = await api.get<CallCenterAgentSettings>(`/api/settings/${me.voiceCloudProductId}/call-center-default-agent-settings`)
            return data
        },
        [me]
    )
    const {
        data,
        isFetching,
        error,
        refetch
    } = useFetchData<CallCenterAgentSettings>(useCallback(() => fetchData(), [fetchData]))

    const onSubmit = useCallback((onSucceeded: () => void) =>
            async (formData: CallCenterAgentSettings) => {
                if (!me) return
                await api.put(`/api/settings/${me.voiceCloudProductId}/call-center-default-agent-settings`, formData)
                await refetch()
                onSucceeded();
            },
        [me, refetch]
    )

    const renderGeneralForm = useCallback(
        (callCenterAgentSettings: CallCenterAgentSettings, cancelEditButton: ReactElement, onSucceeded: () => void): ReactNode =>
        <CallCenterAgentForm callCenterAgentSettings={callCenterAgentSettings} onSubmit={onSubmit(onSucceeded)} buildFormButtons={buildFormButtons(cancelEditButton)}/>
        , [onSubmit, buildFormButtons])

    const guardTimeLabelValues = useCallback(
        (value: CallCenterAgentSettings): LabelValueType[] => [
            {
                label: t('settings.call-center-agent.use-system-default'),
                value: <>{value.useSystemDefaultGuardTimer ? t('general.yes') : t('general.no')}</>
            },
            ...(value.useSystemDefaultGuardTimer? [] :
                [
                    {
                        label: t('settings.call-center-agent.guard-timer'),
                        value: <>{value.enableGuardTimer ? t('settings.call-center-agent.guard-timer-seconds-value', {seconds : value.guardTimerSeconds}) : t('general.no')}</>
                    }
                ])
        ], [t])

    const unavailabilityLabelValues = useCallback(
        (value: CallCenterAgentSettings): LabelValueType[] => [
            {
                label: t('settings.call-center-agent.use-system-default'),
                value: <>{value.useSystemDefaultUnavailableSettings ? t('general.yes') : t('general.no')}</>
            },
            ...(value.useSystemDefaultUnavailableSettings? [] :
                [
                    {
                        label: t('settings.call-center-agent.force-au-on-dnd-activation'),
                        value: <>{value.forceAgentUnavailableOnDNDActivation ? t('general.yes') : t('general.no')}</>
                    },
                    {
                        label: t('settings.call-center-agent.force-au-on-personal-calls'),
                        value: <>{value.forceAgentUnavailableOnPersonalCalls ? t('general.yes') : t('general.no')}</>
                    },
                    {
                        label: t('settings.call-center-agent.force-au-on-bounced-call-limit'),
                        value: <>{value.forceAgentUnavailableOnBouncedCallLimit ?
                            t('settings.call-center-agent.bounces-nr-of', {bounces: value.numberConsecutiveBouncedCallsToForceAgentUnavailable}) : t('general.no')}</>
                    },
                    {
                        label: t('settings.call-center-agent.force-au-on-not-reachable'),
                        value: <>{value.forceAgentUnavailableOnNotReachable ? t('general.yes') : t('general.no')}</>
                    }
                ])
        ], [t])

    const wrapUpLabelValues = useCallback(
        (value: CallCenterAgentSettings): LabelValueType[] => [
            {
                label: t('settings.call-center-agent.wrap-up-destination'),
                value: <>{!!value.wrapUpDestination ? value.wrapUpDestination : t('general.none')}</>
            },
        ], [t])

    const renderDetails = useCallback(
        (callQueueRouting: CallCenterAgentSettings): ReactNode => {
            return (
                <PaperStack>
                    <FormGroup fullWidth={true} key={1} label={t('settings.call-center-agent.guard-timer')}>
                        <LabelValues labelValues={guardTimeLabelValues(callQueueRouting)}/>
                    </FormGroup>
                    <FormGroup fullWidth={true} key={1} label={t('settings.call-center-agent.unavailability-settings')}>
                        <LabelValues labelValues={unavailabilityLabelValues(callQueueRouting)}/>
                    </FormGroup>
                    <FormGroup fullWidth={true} key={1} label={t('settings.call-center-agent.wrap-up-settings')}>
                        <LabelValues labelValues={wrapUpLabelValues(callQueueRouting)}/>
                    </FormGroup>
                </PaperStack>
            )
        },
        [guardTimeLabelValues, unavailabilityLabelValues, wrapUpLabelValues, t]
    )

    return {
        title: t('settings.call-center-agent.title'),
        data: data,
        isFetching: isFetching,
        error: error,
        refetch: refetch,
        renderDetails: renderDetails,
        renderForm: renderGeneralForm,
        enabled: () => true,
        icon: () => <SupportAgent/>
    }
}